import _ from "lodash";
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";

import { getStaffGradesQueryFromCache } from "AppComponents/LEIntoUnitImport/modules/GraphqlHelpers";
import {
  getStaffGradesQuery,
  getOrganizationGradesQuery,
} from "AppComponents/LEIntoUnitImport/modules/Queries";
import { getGradeOptionsMemoized } from "AppComponents/LEIntoUnitImport/modules/Utils";
import {
  updateGradeId,
  updateCurriculumType,
  updateSelectedUnit,
  updateFilters,
} from "AppComponents/LEIntoUnitImport/modules/Module";
import {
  checkHasLibrarySuperAdminAccess,
  getCurriculumProgramsWithAllClassAccess,
} from "ToddleLibrary/modules/ToddleLibraryUtils";

import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import withLoader from "UIComponents/GqlWithLoader";

import { SideNavigation } from "@toddle-edu/ds-web";
import { isSchoolLibraryRoute } from "Teacher/routes/SchoolLibrary/modules/Utils";
import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import { MYP_GRADE_LABELS } from "ToddleLibrary/modules/Constants";
import { getCurrentAcademicYear } from "modules/CommonSelectors";

const styles = {
  navigationContainer: {
    height: "100%",
    width: "272px",
  },
};

function GradeList({
  isDPSchoolLibrary,
  gradeList,
  updateGradeId,
  updateCurriculumType,
  updateSelectedUnit,
  updateFilters,
  globalFilter,
  isSchoolLibrary,
}) {
  let isDPLibrary = false;
  let isMypGrade = false;

  if (!isSchoolLibrary) {
    const globalGradeLabel = _.get(globalFilter, "grade.label", "");
    isDPLibrary = _.isEqual(globalGradeLabel, "Grade 11 & 12");
    isMypGrade = _.includes(MYP_GRADE_LABELS, globalGradeLabel);
  }

  const {
    gradeOptions: sideNavOptions,
    defaultOption,
  } = getGradeOptionsMemoized({
    gradeList,
    isDPLibrary,
    isDPSchoolLibrary,
    isMypGrade,
  });

  const getIdFromKey = key => {
    const [gradeId, curriculumType] = _.split(key, "-");
    return { gradeId, curriculumType };
  };

  const [selectedGrade, setSelectedGrade] = useState(defaultOption.key);

  const updateGradeAndCurriculumTypeInRedux = key => {
    const { gradeId, curriculumType } = getIdFromKey(key);
    updateGradeId(gradeId);
    updateCurriculumType(curriculumType);
  };

  useEffect(() => {
    updateGradeAndCurriculumTypeInRedux(defaultOption.key);
  }, []);

  const onGradeClick = key => {
    if (!_.isEqual(key, selectedGrade)) {
      setSelectedGrade(key);
      updateGradeAndCurriculumTypeInRedux(key);
      // Clear the search text, filters and selected unit
      updateSelectedUnit({});
      const params = { unitType: {}, subject: {}, creator: {}, searchText: "" };
      updateFilters(params);
    }
  };

  return (
    <SideNavigation
      dsVersion="2.0"
      options={sideNavOptions}
      theme="subtle"
      selectedOptionKey={selectedGrade}
      containerStyle={styles.navigationContainer}
      onClick={onGradeClick}
      testId="appComponents-leIntoUnitImport-gradeList"
    />
  );
}

const mapActionCreators = {
  updateGradeId,
  updateCurriculumType,
  updateSelectedUnit,
  updateFilters,
};

const mapStateToProps = (state, ownProps) => {
  const { isCurriculumSelectionDisabled } = ownProps;

  const platformUserInfo = getUserInfo({ portalType: "PLANNER" });
  const userId = platformUserInfo.id;
  const orgId = platformUserInfo.org_id;

  const currentCurriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    ""
  );

  const currentCurriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    ""
  );

  const currentAcademicYear = getCurrentAcademicYear({ organizationId: orgId });
  const currentAcademicYearId = _.get(currentAcademicYear, "id");

  const curriculumProgramIds =
    isCurriculumSelectionDisabled && currentCurriculumProgramId
      ? [currentCurriculumProgramId]
      : [];

  const commonQueryVariables = {
    portalType: "PLANNER", // Need to pass this to send platform user JWT in request header,
    filters: { curriculumProgramIds },
    academicYears: currentAcademicYearId ? [currentAcademicYearId] : [],
  };

  const staffGradeQueryVariables = {
    id: userId,
    ...commonQueryVariables,
  };

  const organizationGradeQueryVariables = {
    id: orgId,
    ...commonQueryVariables,
  };

  const curriculumProgramsWithAllClassAccess = getCurriculumProgramsWithAllClassAccess(
    state
  );

  const hasLibrarySuperAdminAccess = checkHasLibrarySuperAdminAccess();

  const shouldShowOrganizationCourses =
    hasLibrarySuperAdminAccess &&
    !_.isEmpty(curriculumProgramsWithAllClassAccess);

  const isSchoolLibrary = isSchoolLibraryRoute();
  const globalFilter = _.get(state, "toddleLibrary.globalFilter", {});
  const isDPSchoolLibrary =
    currentCurriculumProgramType === CURRICULUM_TYPE_DP && isSchoolLibrary;

  return {
    userId,
    orgId,
    curriculumProgramsWithAllClassAccess,
    staffGradeQueryVariables,
    organizationGradeQueryVariables,
    shouldShowOrganizationCourses,
    isDPSchoolLibrary,
    globalFilter,
    isSchoolLibrary,
    isData: true,
    isLoading: false,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getStaffGradesQuery, {
    name: "getStaffGrades",
    skip: ({ userId }) => !userId,
    options: ({ staffGradeQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: staffGradeQueryVariables,
    }),
    props({
      getStaffGrades,
      ownProps: { staffGradeQueryVariables, isData, isLoading },
    }) {
      const queryData = getStaffGradesQueryFromCache(staffGradeQueryVariables);
      const gradeList = _.get(queryData, "allGrades");

      return {
        isData: !_.isEmpty(gradeList) && isData,
        isLoading:
          isLoading || _.includes([1, 2], getStaffGrades.networkStatus),
        gradeList,
      };
    },
  }),
  graphql(getOrganizationGradesQuery, {
    name: "getOrganizationGrades",
    skip: ({ orgId, shouldShowOrganizationCourses }) =>
      !orgId || !shouldShowOrganizationCourses,
    options: ({ organizationGradeQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: organizationGradeQueryVariables,
    }),
    props({
      getOrganizationGrades,
      ownProps: { gradeList, curriculumProgramsWithAllClassAccess, isLoading },
    }) {
      const queryData = _.get(getOrganizationGrades, "node", {});

      const organizationGradeList = _.get(queryData, "grades");

      const organizationGradesWithAllClassAccess = _.filter(
        organizationGradeList,
        grade =>
          _.some(curriculumProgramsWithAllClassAccess, {
            id: _.get(grade, "curriculumProgram.id"),
          })
      );

      gradeList = _.unionBy(
        organizationGradesWithAllClassAccess,
        gradeList,
        "id"
      );

      return {
        isData: !_.isEmpty(gradeList),
        isLoading:
          isLoading || _.includes([1, 2], getOrganizationGrades.networkStatus),
        gradeList,
      };
    },
  }),
  withLoader
)(GradeList);
